// Function index:
//   showBuilderScreen(worldId) — boot the world-builder screen. Paint terrain
//       by left-click; shift+click to select a hex for the side-panel
//       inspector. Save flushes accumulated changes via PUT /worlds/:id/hexes/bulk.
//
// State flow:
//   `state`  — local Map<key, HexState> hydrated from the server.
//   `dirty`  — set of keys edited locally; cleared on successful Save.

import { Application, Container, Graphics, type FederatedPointerEvent } from 'pixi.js';
import type { HexCoordinates } from 'honeycomb-grid';
import { bulkUpdateHexes, getWorld, type HexPatch, type HexRow } from '../api.js';
import { buildGrid, drawHex, drawSelectionRing, installPanZoom } from './builder/scene.js';
import { mountPalette } from './builder/palette.js';
import { mountNpcPanel } from './builder/npc-panel.js';
import { colorFor, key, type HexState } from './builder/types.js';

export async function showBuilderScreen(worldId: number): Promise<void> {
  const $ = (id: string): HTMLElement => document.getElementById(id)!;
  const screen = $('screen-builder');
  screen.hidden = false;

  const { world, hexes } = await getWorld(worldId);
  $('builder-world-name').textContent = world.name;
  $('builder-world-meta').textContent = `${world.setting} · ${world.grid_width}×${world.grid_height}`;

  const state = new Map<string, HexState>();
  for (const h of hexes as HexRow[]) {
    state.set(key(h.q, h.r), {
      q: h.q, r: h.r,
      terrain: h.terrain,
      name: h.name,
      description: h.description,
      lore: h.lore,
    });
  }
  const dirty = new Set<string>();
  const setDirty = (k: string): void => {
    dirty.add(k);
    ($('builder-dirty') as HTMLSpanElement).hidden = false;
  };

  const canvasParent = $('builder-canvas');
  const app = new Application();
  await app.init({ resizeTo: canvasParent, background: 0x0a0a0a, antialias: true });
  canvasParent.appendChild(app.canvas);

  const camera = new Container();
  app.stage.addChild(camera);
  const tileLayer = new Container();
  const overlayLayer = new Container();
  camera.addChild(tileLayer);
  camera.addChild(overlayLayer);

  const grid = buildGrid(state.values());
  const tileGraphics = new Map<string, Graphics>();

  grid.forEach((hex) => {
    const k = key(hex.q, hex.r);
    const s = state.get(k);
    if (!s) return;
    const g = new Graphics();
    drawHex(g, hex.corners, colorFor(s.terrain));
    g.eventMode = 'static';
    g.cursor = 'pointer';
    g.on('pointerdown', (e: FederatedPointerEvent) => onTilePointerDown(e, k));
    tileLayer.addChild(g);
    tileGraphics.set(k, g);
  });

  const selectionRing = new Graphics();
  overlayLayer.addChild(selectionRing);

  const origin = grid.getHex({ q: 0, r: 0 } as HexCoordinates) ?? grid.toArray()[0];
  if (origin) {
    camera.x = canvasParent.clientWidth / 2 - origin.x;
    camera.y = canvasParent.clientHeight / 2 - origin.y;
  }

  installPanZoom(app.canvas as HTMLCanvasElement, camera);

  let activeTerrain = 'grass';
  mountPalette($('terrain-palette'), (t) => { activeTerrain = t; });

  let selectedKey: string | null = null;
  const noneEl = $('hex-none') as HTMLParagraphElement;
  const hexForm = $('hex-form') as HTMLFormElement;
  const coordsEl = $('hex-coords') as HTMLParagraphElement;

  const npcPanel = await mountNpcPanel(worldId, () => {
    if (!selectedKey) return null;
    const s = state.get(selectedKey);
    return s ? { q: s.q, r: s.r } : null;
  });

  const selectHex = (k: string): void => {
    const s = state.get(k);
    if (!s) return;
    selectedKey = k;
    noneEl.hidden = true;
    hexForm.hidden = false;
    coordsEl.textContent = `(${s.q}, ${s.r})`;
    (hexForm.elements.namedItem('terrain') as HTMLInputElement).value = s.terrain;
    (hexForm.elements.namedItem('name') as HTMLInputElement).value = s.name ?? '';
    (hexForm.elements.namedItem('description') as HTMLTextAreaElement).value = s.description;
    (hexForm.elements.namedItem('lore') as HTMLTextAreaElement).value = s.lore;
    npcPanel.refresh({ q: s.q, r: s.r });
    const hex = grid.getHex({ q: s.q, r: s.r } as HexCoordinates);
    if (hex) drawSelectionRing(selectionRing, hex.corners);
  };

  ($('hex-apply') as HTMLButtonElement).addEventListener('click', () => {
    if (!selectedKey) return;
    const s = state.get(selectedKey);
    if (!s) return;
    s.terrain = (hexForm.elements.namedItem('terrain') as HTMLInputElement).value.trim() || 'unknown';
    s.name = ((hexForm.elements.namedItem('name') as HTMLInputElement).value.trim()) || null;
    s.description = (hexForm.elements.namedItem('description') as HTMLTextAreaElement).value;
    s.lore = (hexForm.elements.namedItem('lore') as HTMLTextAreaElement).value;
    setDirty(selectedKey);
    repaint(selectedKey);
  });

  function onTilePointerDown(e: FederatedPointerEvent, k: string): void {
    if (e.button !== 0) return;
    if (e.shiftKey) {
      selectHex(k);
      return;
    }
    paint(k);
  }

  function paint(k: string): void {
    const s = state.get(k);
    if (!s || s.terrain === activeTerrain) return;
    s.terrain = activeTerrain;
    setDirty(k);
    repaint(k);
  }

  function repaint(k: string): void {
    const s = state.get(k);
    const g = tileGraphics.get(k);
    if (!s || !g) return;
    const hex = grid.getHex({ q: s.q, r: s.r } as HexCoordinates);
    if (!hex) return;
    g.clear();
    drawHex(g, hex.corners, colorFor(s.terrain));
  }

  const saveBtn = $('builder-save') as HTMLButtonElement;
  const doneBtn = $('builder-done') as HTMLButtonElement;
  const dirtyTag = $('builder-dirty') as HTMLSpanElement;

  saveBtn.addEventListener('click', async () => {
    if (dirty.size === 0) return;
    const patches: HexPatch[] = [];
    for (const k of dirty) {
      const s = state.get(k);
      if (!s) continue;
      let lore: unknown = {};
      try { lore = JSON.parse(s.lore || '{}'); } catch { /* leave default */ }
      patches.push({
        q: s.q, r: s.r,
        terrain: s.terrain,
        name: s.name,
        description: s.description,
        lore,
      });
    }
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      await bulkUpdateHexes(worldId, patches);
      dirty.clear();
      dirtyTag.hidden = true;
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  });

  return new Promise<void>((resolve) => {
    doneBtn.addEventListener('click', async () => {
      if (dirty.size > 0 && !confirm('Discard unsaved changes?')) return;
      app.destroy(true, { children: true });
      canvasParent.innerHTML = '';
      screen.hidden = true;
      resolve();
    }, { once: true });
  });
}
